import * as Fields from "#root/components/form/fields";
import StandardEditor from "#root/components/standard-editor";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  currencyCollection,
  domicileCollection,
  exchangeCollection,
  type ExchangeListItem,
  holidayGroupCollection,
  symbolTypeCollection
} from "#root/schema/platform/reference-data";
import PageLayout from "./layout";

export default function ({ exchangeId }): ReactNode {
  const navigate = useNavigate();
  const handleUpdate = useCallback(
    (data: RecordModificationResponse<ExchangeListItem>) => {
      if ("deleted" in data) {
        navigate("/admin/platform/reference/exchange");
      } else if ("created" in data) {
        navigate(`/admin/platform/reference/exchange/${data.created}`);
      }
    },
    [navigate],
  );

  return (
    <PageLayout title="Reference Data Platform - Edit Exchange">
      <section>
        <h2>{exchangeId === "new" ? "Create" : "Edit"} Exchange</h2>
        <StandardEditor id={exchangeId} collection={exchangeCollection} onSave={handleUpdate} onDelete={handleUpdate}>
          <Fields.Set name="id">
            <label>Exchange Id</label>
            <Fields.Integer required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="name">
            <label>Exchange Name</label>
            <Fields.String required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="shortName">
            <label>Exchange Short Name</label>
            <Fields.String required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="prefixCode">
            <label>Exchange Prefix Code</label>
            <Fields.String required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="entitlementCode">
            <label>Exchange Entitlement Code</label>
            <Fields.String required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="mic">
            <label>Exchange MIC Code</label>
            <Fields.String required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="delay">
            <label>Delay</label>
            <Fields.TimeInterval required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="updateType">
            <label>Type of Updates</label>
            <Fields.Select>
              <Fields.Option value="STREAMING">Streaming Data</Fields.Option>
              <Fields.Option value="BATCH">Batch</Fields.Option>
              <Fields.Option value="EOD">EOD</Fields.Option>
            </Fields.Select>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="sessionStartTime">
            <label>Session Start Time</label>
            <Fields.TimeOfDay required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="chartStartTime">
            <label>Chart Start Time</label>
            <Fields.TimeOfDay required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="chartEndTime">
            <label>Chart End Time</label>
            <Fields.TimeOfDay required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="timeZone">
            <label>Time Zone</label>
            <Fields.Timezone required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="flushHandler">
            <label>Flush Handler for Exchange</label>
            <Fields.Select>
              <Fields.Option value="default">
                Default - Exchange automatically flushes at the session start time, removing the open/high/low/volume, and setting the previous close to the
                price
              </Fields.Option>
              <Fields.Option value="NoFlush">No Flush - Exchange does not flush at the session start</Fields.Option>
            </Fields.Select>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="tier">
            <label>
              Exchange Tier (legacy data systems)- Controls the order in which ticker symbols are matched when a ticker matches on multiple exchanges. Lower
              tiers are preferred
            </label>
            <Fields.Integer required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="popularity">
            <label>
              Exchange Popularity - Controls the order in which ticker symbols are matched when a ticker matches on multiple exchanges. Higher popularity
              exchanges are preferred
            </label>
            <Fields.Integer required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="defaultCurrencyId">
            <label>Default Currency for Exchange</label>
            <Fields.AutoComplete required collection={currencyCollection}/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="domicileId">
            <label>Domicile (Country) for Exchange</label>
            <Fields.AutoComplete required collection={domicileCollection}/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="defaultSymbolType">
            <label>Default Symbol Type for Exchange</label>
            <Fields.AutoComplete required collection={symbolTypeCollection}/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="holidayGroup">
            <label>Holiday Group - The holiday group to associate this exchange with</label>
            <Fields.AutoComplete collection={holidayGroupCollection}/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="indexTags">
            <label>Index Tags</label>
            <Fields.JSONEditor rows={10}/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="indexTags">
            <label>Index Tags</label>
            <Fields.JSONEditor rows={10}/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="searchPrefixes">
            <label>Search Prefixes</label>
            <Fields.JSONEditor rows={10}/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="searchSuffixes">
            <label>Search Suffixes</label>
            <Fields.JSONEditor rows={10}/>
            <Fields.Validity/>
          </Fields.Set>
        </StandardEditor>
      </section>
    </PageLayout>
  );
}
